// Arena HUD view-models (lane C, backlog C-8, C-9, C-11, C-13, C-17; territory-ui.md). Pure and
// DOM-free so every rule is unit-tested: the scale model (UI §1), the land pill and its track
// (§2.1), the leaderboard bars (§2.2), the kill feed lines (§2.4), the banner queue (§5), the
// minimap box (§3) and the results-screen timeline (§4.2).

import {
  ARENA_CAUSE_CODE,
  type ArenaLifeInfo,
  type LeaderboardEntry,
  type Rgb,
} from '@ballio/shared';
import { t } from '../ui/i18n/t.js';

// --- §1 scale model ---------------------------------------------------------------------------

export interface HudScale {
  /** Screen scale: diagonal / 1567. */
  readonly s: number;
  /** Font F = A(20, 30) · s. */
  readonly F: number;
  /** Padding P = 16 · s. */
  readonly P: number;
  /** 3D lip L = 4 · s. */
  readonly L: number;
  /** Bar height H = 1.5 F. */
  readonly H: number;
  /** Bar width W = width / A(4, 2.25). */
  readonly W: number;
  /** Minimap side M = width / A(8, 3). */
  readonly M: number;
}

/** A(a, b): `a` at aspect ≥ 16:9, `b` at ≤ 9:16, linear in the aspect ratio between. */
export function aspectLerp(a: number, b: number, width: number, height: number): number {
  const wide = 16 / 9;
  const tall = 9 / 16;
  const r = height > 0 ? width / height : wide;
  if (r >= wide) return a;
  if (r <= tall) return b;
  return b + ((a - b) * (r - tall)) / (wide - tall);
}

export function hudScale(width: number, height: number): HudScale {
  const s = Math.hypot(width, height) / 1567 || 1;
  const F = aspectLerp(20, 30, width, height) * s;
  return {
    s,
    F,
    P: 16 * s,
    L: 4 * s,
    H: 1.5 * F,
    W: width / aspectLerp(4, 2.25, width, height),
    M: width / aspectLerp(8, 3, width, height),
  };
}

// --- §2.1 land pill ---------------------------------------------------------------------------

/** "x.xx%" (always 2 decimals). */
export function formatPct(pct: number): string {
  const v = Number.isFinite(pct) ? Math.max(0, pct) : 0;
  return `${v.toFixed(2)}%`;
}

/**
 * The pill width: W × (0.25 + 0.75 × min(1, current / best)); the full width when there is no
 * best yet (a stored 0 counts as none).
 */
export function landPillWidth(
  barWidth: number,
  currentPct: number,
  bestPct: number | null,
): number {
  if (bestPct === null || !(bestPct > 0)) return barWidth;
  const r = Math.min(1, Math.max(0, currentPct / bestPct));
  return barWidth * (0.25 + 0.75 * r);
}

// --- §2.2 leaderboard -------------------------------------------------------------------------

export interface ArenaBoardRow {
  readonly rank: number;
  readonly text: string;
  /** Visible bar length (CSS px); the bar runs off the right edge. */
  readonly length: number;
  readonly isYou: boolean;
  /** Slot 0–4 for the top 5; 6 for the own row outside them (slot 5 stays empty). */
  readonly slot: number;
  /** The player's main colour (the row is drawn in it, visuals §5), null = neutral row. */
  readonly color: Rgb | null;
}

/** Compact USD: "$12.40", "$1.2k" from 1,000. */
export function compactUsd(usd: number): string {
  const v = Number.isFinite(usd) ? Math.max(0, usd) : 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
}

/**
 * Bar lengths for the board's amounts (best first): W/2 + (W/2) × (amount / leader's), each at
 * least 5 % of W/2 shorter than the row above (monotonic even on ties).
 */
export function boardBarLengths(amounts: readonly number[], barWidth: number): number[] {
  const half = barWidth / 2;
  const lead = amounts[0] ?? 0;
  const out: number[] = [];
  let prev = Number.POSITIVE_INFINITY;
  for (const a of amounts) {
    const share = lead > 0 ? Math.max(0, Math.min(1, a / lead)) : 1;
    let len = half + half * share;
    if (len > prev - 0.05 * half) len = prev - 0.05 * half;
    len = Math.max(0, len);
    out.push(len);
    prev = len;
  }
  return out;
}

/** "N – $B · P.PP% Name" (UI §2.2), the bag compact. */
export function boardRowText(
  rank: number,
  bag: string | null,
  landPct: number | undefined,
  name: string,
): string {
  const parts: string[] = [];
  if (bag) parts.push(bag);
  if (landPct !== undefined) parts.push(formatPct(landPct));
  const mid = parts.join(' · ');
  return `${rank} – ${mid}${mid ? ' ' : ''}${name || t('arena.unnamed')}`;
}

/**
 * The board rows: the top 5 by bag, plus the own row in slot 7 (index 6) when outside them and
 * playing. `formatBag` turns an entry amount (lamports) into compact text; `colorOf` gives a row's
 * player colour (null = neutral; LEADERBOARD rows carry names, not player ids).
 */
export function arenaBoardRows(
  entries: readonly LeaderboardEntry[],
  yourRank: number,
  yourAmount: number | undefined,
  yourLandPct: number | undefined,
  yourName: string,
  barWidth: number,
  formatBag: (amount: number) => string | null,
  colorOf: (name: string, isYou: boolean) => Rgb | null = () => null,
): ArenaBoardRow[] {
  const top = entries.slice(0, 5);
  const amounts = top.map((e) => e.amount ?? 0);
  const lengths = boardBarLengths(amounts, barWidth);
  const rows: ArenaBoardRow[] = top.map((e, i) => ({
    rank: i + 1,
    text: boardRowText(
      i + 1,
      e.amount !== undefined ? formatBag(e.amount) : null,
      e.landPct,
      e.name,
    ),
    length: lengths[i] as number,
    isYou: e.isYou,
    slot: i,
    color: colorOf(e.name, e.isYou),
  }));
  if (yourRank > 5) {
    const lead = amounts[0] ?? 0;
    const half = barWidth / 2;
    const share = lead > 0 && yourAmount !== undefined ? Math.min(1, yourAmount / lead) : 0;
    const floor = (lengths[lengths.length - 1] ?? barWidth) - 0.05 * half;
    rows.push({
      rank: yourRank,
      text: boardRowText(
        yourRank,
        yourAmount !== undefined ? formatBag(yourAmount) : null,
        yourLandPct,
        yourName,
      ),
      length: Math.min(floor, half + half * share),
      isYou: true,
      slot: 6,
      color: colorOf(yourName, true),
    });
  }
  return rows;
}

// --- §2.4 kill feed ---------------------------------------------------------------------------

/** One kill feed line (UI §8 copy): cut / enclosed (also an exit capture) / starved / crashed. */
export function arenaFeedText(cause: number, killer: string, victim: string, usd: string): string {
  const k = killer || t('arena.unnamed');
  const v = victim || t('arena.unnamed');
  switch (cause) {
    case ARENA_CAUSE_CODE.CUT:
      return t('arena.feed.cut', { killer: k, victim: v, usd });
    case ARENA_CAUSE_CODE.ENCLOSED:
    case ARENA_CAUSE_CODE.EXIT_CAPTURED:
      return t('arena.feed.enclosed', { killer: k, victim: v, usd });
    case ARENA_CAUSE_CODE.STARVED:
      return t('arena.feed.starved', { killer: k, victim: v, usd });
    default:
      return t('arena.feed.spill', { victim: v, usd });
  }
}

// --- §5 banner queue --------------------------------------------------------------------------

export const BANNER_TIMING = Object.freeze({ inMs: 500, holdMs: 3000, outMs: 500, gapMs: 250 });

export interface BannerItem {
  readonly key: string;
  readonly title: string;
  readonly detail: string;
  readonly icon: 'shield' | 'coin' | 'plug' | 'clock' | 'info';
}

export interface BannerFrame {
  readonly item: BannerItem;
  /** 0 = fully above the screen, 1 = fully shown. */
  readonly shown: number;
}

function easeOutCubic(x: number): number {
  const u = 1 - Math.min(1, Math.max(0, x));
  return 1 - u * u * u;
}

/**
 * One banner at a time: slides down (easeOutCubic, 500 ms), holds 3000 ms, slides up (500 ms),
 * then waits 250 ms. The same key while showing extends the hold instead of queueing.
 */
export class BannerQueue {
  private readonly queue: BannerItem[] = [];
  private current: { item: BannerItem; startMs: number; holdUntilMs: number } | null = null;
  private nextAtMs = Number.NEGATIVE_INFINITY;
  /** A banner started (in `push` or `frame`) and the next `frame` has not reported it yet. */
  private unreported = false;

  /** Adds a banner; returns true when it starts showing now (the `notice` sound). */
  push(item: BannerItem, nowMs: number): boolean {
    const c = this.current;
    if (c && c.item.key === item.key) {
      const outStart = c.holdUntilMs;
      if (nowMs < outStart) {
        c.holdUntilMs = nowMs + BANNER_TIMING.holdMs;
        return false;
      }
    }
    if (this.queue.some((q) => q.key === item.key)) return false;
    this.queue.push(item);
    return this.advance(nowMs);
  }

  /** Drops a queued or showing banner by key (it slides out if showing). */
  drop(key: string, nowMs: number): void {
    const i = this.queue.findIndex((q) => q.key === key);
    if (i >= 0) this.queue.splice(i, 1);
    const c = this.current;
    if (c && c.item.key === key && nowMs < c.holdUntilMs) {
      c.holdUntilMs = Math.max(nowMs, c.startMs + BANNER_TIMING.inMs);
    }
  }

  clear(): void {
    this.queue.length = 0;
    this.current = null;
    this.nextAtMs = Number.NEGATIVE_INFINITY;
    this.unreported = false;
  }

  /** Starts the next banner when due; true if one started. */
  private advance(nowMs: number): boolean {
    const c = this.current;
    if (c && nowMs >= c.holdUntilMs + BANNER_TIMING.outMs) {
      this.current = null;
      this.nextAtMs = c.holdUntilMs + BANNER_TIMING.outMs + BANNER_TIMING.gapMs;
    }
    if (this.current || nowMs < this.nextAtMs) return false;
    const item = this.queue.shift();
    if (!item) return false;
    this.current = {
      item,
      startMs: nowMs,
      holdUntilMs: nowMs + BANNER_TIMING.inMs + BANNER_TIMING.holdMs,
    };
    this.unreported = true;
    return true;
  }

  /**
   * The banner to draw at `nowMs` (null = none); `started` is true on the first frame of every
   * banner, also one that `push` started at once (its `notice` sound plays there).
   */
  frame(nowMs: number): { frame: BannerFrame | null; started: boolean } {
    this.advance(nowMs);
    const started = this.unreported;
    this.unreported = false;
    const c = this.current;
    if (!c) return { frame: null, started };
    let shown: number;
    if (nowMs < c.startMs + BANNER_TIMING.inMs)
      shown = easeOutCubic((nowMs - c.startMs) / BANNER_TIMING.inMs);
    else if (nowMs < c.holdUntilMs) shown = 1;
    else shown = 1 - easeOutCubic((nowMs - c.holdUntilMs) / BANNER_TIMING.outMs);
    return { frame: { item: c.item, shown: Math.max(0, Math.min(1, shown)) }, started };
  }
}

// --- §4.2 results timeline ---------------------------------------------------------------------

/** CSS `ease` = cubic-bezier(0.25, 0.1, 0.25, 1). */
export function cssEase(x: number): number {
  return cubicBezier(0.25, 0.1, 0.25, 1, x);
}

/** A CSS cubic-bezier(x1, y1, x2, y2) timing function at progress `x` (0–1). */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number, x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bx = (u: number): number => 3 * x1 * u * (1 - u) ** 2 + 3 * x2 * u * u * (1 - u) + u ** 3;
  const by = (u: number): number => 3 * y1 * u * (1 - u) ** 2 + 3 * y2 * u * u * (1 - u) + u ** 3;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (bx(mid) < x) lo = mid;
    else hi = mid;
  }
  return by((lo + hi) / 2);
}

export const RESULTS_TIMING = Object.freeze({
  /** Each row fades in over this long… */
  rowFadeMs: 2000,
  /** …starting this long after the previous one (rows at 0, 1, 2, 3 s). */
  rowStaggerMs: 1000,
  rows: 4,
  /** Continue fades 4 → 6 s. */
  continueStartMs: 4000,
  continueFadeMs: 2000,
  /** A click, tap or Enter after this completes every fade and enables Continue. */
  skipAfterMs: 1000,
});

/** Row `i`'s opacity `ms` after the results opened (null skip = no skip yet). */
export function resultsRowOpacity(
  i: number,
  ms: number,
  skippedAtMs: number | null = null,
): number {
  if (skippedAtMs !== null && ms >= skippedAtMs) return 1;
  const start = i * RESULTS_TIMING.rowStaggerMs;
  return cssEase((ms - start) / RESULTS_TIMING.rowFadeMs);
}

export function resultsContinueOpacity(ms: number, skippedAtMs: number | null = null): number {
  if (skippedAtMs !== null && ms >= skippedAtMs) return 1;
  return cssEase((ms - RESULTS_TIMING.continueStartMs) / RESULTS_TIMING.continueFadeMs);
}

/** Continue is clickable once its opacity reaches 0.5 (4.59 s) or after a skip. */
export function resultsContinueEnabled(ms: number, skippedAtMs: number | null = null): boolean {
  return resultsContinueOpacity(ms, skippedAtMs) >= 0.5;
}

/** Whether a click / tap / Enter at `ms` skips the fades. */
export function resultsCanSkip(ms: number): boolean {
  return ms >= RESULTS_TIMING.skipAfterMs;
}

// --- §4 results rows --------------------------------------------------------------------------

export type ArenaResultKind = 'death' | 'cashOut';

export interface ArenaResultInput {
  readonly kind: ArenaResultKind;
  readonly info: ArenaLifeInfo | null;
  /** Lamports: lost (death) or banked (cash-out). */
  readonly value: number | null;
  /** The value as label text. */
  readonly valueText: string | null;
  readonly timeAliveSec: number;
  /** The best max-land % stored before this life (null = none). */
  readonly previousBestPct: number | null;
}

export interface ArenaResultView {
  readonly outcome: string;
  readonly money: string | null;
  readonly maxLand: string;
  readonly newBest: boolean;
  readonly timeKills: string;
}

/** "MM:SS". */
export function formatClock(sec: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(sec) ? sec : 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** The four rows of the arena results (UI §4.2, copy §8). */
export function arenaResultView(r: ArenaResultInput): ArenaResultView {
  const info = r.info;
  const name = info?.killerName || t('arena.unnamed');
  const usd = r.valueText ?? '';
  let outcome: string;
  let money: string | null = null;
  if (r.kind === 'cashOut') {
    outcome = info?.forcedExit
      ? t('arena.result.auto', { usd })
      : t('arena.result.banked', { usd });
    money = r.valueText !== null ? t('arena.result.banked', { usd }) : null;
  } else {
    const cause = info?.cause ?? ARENA_CAUSE_CODE.NONE;
    switch (cause) {
      case ARENA_CAUSE_CODE.CUT:
        outcome = t('arena.result.cut', { name });
        break;
      case ARENA_CAUSE_CODE.ENCLOSED:
      case ARENA_CAUSE_CODE.EXIT_CAPTURED:
        outcome = t('arena.result.enclosed', { name });
        break;
      case ARENA_CAUSE_CODE.STARVED:
        outcome = t('arena.result.starved', { name });
        break;
      default:
        outcome = info?.atWall ? t('arena.result.selfWall') : t('arena.result.self');
    }
    if (r.valueText !== null && r.value !== null && r.value > 0) {
      money =
        cause === ARENA_CAUSE_CODE.SELF || cause === ARENA_CAUSE_CODE.NONE
          ? t('arena.result.dropped', { usd })
          : t('arena.result.lost', { usd, name });
    }
  }
  const maxPct = info?.maxLandPct ?? 0;
  const newBest = maxPct > 0 && (r.previousBestPct === null || maxPct > r.previousBestPct);
  return {
    outcome,
    money,
    maxLand: formatPct(maxPct),
    newBest,
    timeKills: t('arena.result.timeKills', {
      time: formatClock(r.timeAliveSec),
      n: info?.kills ?? 0,
    }),
  };
}

/** Results open this long after the life ended (UI §4.1, §4.3). */
export function resultsDelayMs(
  kind: ArenaResultKind,
  cause: number,
  delays: {
    readonly self: number;
    readonly killed: number;
    readonly exit: number;
  },
): number {
  if (kind === 'cashOut') return delays.exit;
  return cause === ARENA_CAUSE_CODE.SELF || cause === ARENA_CAUSE_CODE.NONE
    ? delays.self
    : delays.killed;
}
